import { getBrowserApiUrl } from '@/utils/apiEndpoint';

import type { BoardContentUpdateType } from '@/types/boardContent';

/**
 * ### InquiryFailureReason
 *
 * Why a submission did not produce an inquiry.
 * `invalid_input` means the body failed validation and the visitor has to change something;
 * `unavailable` means the inquiry service could not take the request and retrying later may work.
 */
export type InquiryFailureReason = 'invalid_input' | 'unavailable';

/**
 * ### INQUIRY_FAILURE_MESSAGE_KEYS
 *
 * Translation key of the message shown for each failure reason.
 * Each message names what went wrong and what the visitor can do next.
 */
export const INQUIRY_FAILURE_MESSAGE_KEYS = {
  invalid_input:
    '입력 내용에 확인이 필요한 항목이 있어요. 필수 항목을 다시 확인한 뒤 제출해 주세요',
  unavailable: '지금은 문의를 접수할 수 없어요. 잠시 후 다시 시도해 주세요',
} as const satisfies Record<InquiryFailureReason, string>;

/**
 * ### InquirySubmissionResult
 *
 * Outcome of one submission attempt.
 * The failure member carries a reason code rather than a message so the form can translate it.
 */
export type InquirySubmissionResult =
  | { leadId: string | null; success: true }
  | { reason: InquiryFailureReason; success: false };

/**
 * ### InquirySubmitAction
 *
 * Sends an inquiry body to the board API and reports the outcome.
 * Implementations resolve with a failure result for a rejected request and reject only when
 * the request itself could not be made.
 */
export type InquirySubmitAction = (
  body: BoardContentUpdateType,
) => Promise<InquirySubmissionResult>;

type InquiryApiResponse = {
  data?: {
    board_content?: {
      idx?: unknown;
    };
  };
  message?: string;
  success?: boolean;
};

export function inquiryLeadId(payload: InquiryApiResponse) {
  const value = payload.data?.board_content?.idx;
  if (typeof value === 'number' && Number.isSafeInteger(value) && value > 0) {
    return String(value);
  }
  if (typeof value === 'string' && /^\d+$/.test(value)) return value;
  return null;
}

/**
 * ### submitInquiryFromBrowser
 *
 * Posts the inquiry straight from the browser when no server action is supplied.
 * A rejected request resolves as `unavailable`.
 */
export const submitInquiryFromBrowser: InquirySubmitAction = async (body) => {
  const response = await fetch(getBrowserApiUrl('board-contents/nonuser'), {
    body: JSON.stringify(body),
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  });
  const payload = (await response.json()) as InquiryApiResponse;

  if (!response.ok || !payload.success) {
    console.error('Inquiry API rejected the submission', {
      message: payload.message,
      status: response.status,
    });
    return { reason: 'unavailable', success: false };
  }

  return { leadId: inquiryLeadId(payload), success: true };
};
